import { ReactNode, useEffect } from 'react'
import { on, retrieveLaunchParams, viewport } from '@telegram-apps/sdk'
import useLayoutState from '@/store/useLayoutStore'
import config from '@/config/env'
import { useTheme } from '@/hooks/useTheme'
import { SHOW_MOCK_OFFSETS } from '@/config/app'

const MIN_TOP_OFFSET = 45
const MIN_BOTTOM_OFFSET = 30

const getOffsetsFromSDK = () => {
  if (SHOW_MOCK_OFFSETS) {
    console.log('[OFFSETS] 🎭 getOffsetsFromSDK: MOCK MODE active')
    return {
      top: 100,
      bottom: 50,
      left: 0,
      right: 0,
    }
  }

  try {
    const safeArea = viewport.safeAreaInsets()
    const contentSafeArea = viewport.contentSafeAreaInsets()

    console.log('[OFFSETS] 📱 Raw SDK safeAreaInsets:', safeArea)
    console.log('[OFFSETS] 📱 Raw SDK contentSafeAreaInsets:', contentSafeArea)

    // Sum safeArea + additional offsets if contentSafeArea is present
    const topOffset =
      safeArea.top + (contentSafeArea.top > 0 ? contentSafeArea.top : 0)
    const bottomOffset =
      safeArea.bottom +
      (contentSafeArea.bottom > 0 ? contentSafeArea.bottom : 0)

    console.log('[OFFSETS] 🧮 Before fallback:', {
      top: topOffset,
      bottom: bottomOffset,
    })

    const calculatedOffsets = {
      top: Math.max(topOffset, MIN_TOP_OFFSET),
      bottom: Math.max(bottomOffset, MIN_BOTTOM_OFFSET),
      left: safeArea.left + contentSafeArea.left,
      right: safeArea.right + contentSafeArea.right,
    }
    console.log(
      '[OFFSETS] ✅ Calculated offsets (with fallback):',
      calculatedOffsets,
    )
    return calculatedOffsets
  } catch (error) {
    console.log('[OFFSETS] ⚠️ SDK not ready, using fallback:', error)
    return {
      top: MIN_TOP_OFFSET,
      bottom: MIN_BOTTOM_OFFSET,
      left: 0,
      right: 0,
    }
  }
}

export const OffsetsProvider = ({ children }: { children: ReactNode }) => {
  const setOffsets = useLayoutState((state) => state.setOffsets)
  const setIsAndroid = useLayoutState((state) => state.setIsAndroid)

  useEffect(() => {
    console.log('[OFFSETS] 🚀 OffsetsProvider mounted')
    console.log('[OFFSETS] 🔧 SHOW_MOCK_OFFSETS:', SHOW_MOCK_OFFSETS)
    console.log('[OFFSETS] 🔧 ENV:', config.ENV)

    // Initialize offsets with a delay for SDK readiness
    const timeoutId = setTimeout(() => {
      console.log(
        '[OFFSETS] ⏱️ 100ms delay passed, fetching offsets from SDK...',
      )
      setOffsets(getOffsetsFromSDK())
    }, 100)

    // Subscribe to safe area changes (single subscription for the whole app)
    let removeListener: (() => void) | null = null
    try {
      removeListener = on('safe_area_changed', () => {
        console.log('[OFFSETS] 🔄 safe_area_changed event triggered')
        setOffsets(getOffsetsFromSDK())
      })
      console.log('[OFFSETS] 👂 Subscribed to safe_area_changed events')
    } catch (error) {
      console.log('[OFFSETS] ⚠️ safe_area_changed not available:', error)
    }

    return () => {
      console.log('[OFFSETS] 🛑 OffsetsProvider unmounting')
      clearTimeout(timeoutId)
      if (removeListener) {
        removeListener()
        console.log('[OFFSETS] 👋 Unsubscribed from safe_area_changed')
      }
    }
  }, [setOffsets])

  useEffect(() => {
    const checkIsAndroid = async () => {
      if (config.ENV !== 'local') {
        const launchParams = await retrieveLaunchParams()
        console.log('[launchParams]', launchParams)
        setIsAndroid(launchParams.tgWebAppPlatform === 'android')
      }
    }
    checkIsAndroid()
  }, [setIsAndroid])

  const theme = useTheme()
  const screenBackground = useLayoutState((state) => state.screenBackground)

  return (
    <div
      style={{
        backgroundColor: screenBackground || theme.colors.background.screen,
        color: theme.colors.label.default,
      }}
      className="h-full"
    >
      {children}
    </div>
  )
}

export default OffsetsProvider
